/**
 * Which platform a creator lives on — mirrors spade-discovery's
 * src/platforms/platform.ts. TikTok is the default everywhere so rows from
 * before multi-platform keep working.
 */
export const PLATFORMS = ['tiktok', 'instagram', 'x'] as const;
export type Platform = (typeof PLATFORMS)[number];

export const PLATFORM_LABELS: Record<Platform, string> = {
  tiktok: 'TikTok',
  instagram: 'Instagram',
  x: 'X',
};

export function toPlatform(v: unknown): Platform {
  return v === 'instagram' || v === 'x' ? v : 'tiktok';
}

export function profileUrl(platform: Platform | string | null | undefined, username: string): string {
  const u = encodeURIComponent(username.replace(/^@/, ''));
  const p = toPlatform(platform);
  if (p === 'instagram') return `https://www.instagram.com/${u}/`;
  if (p === 'x') return `https://x.com/${u}`;
  return `https://www.tiktok.com/@${u}`;
}

/**
 * A lead reference that survives mixed-platform folders: the bare handle for
 * TikTok (the old contract, so existing call sites and the server's bulk
 * delete keep working) and "platform:handle" otherwise.
 */
export function leadKey(platform: Platform | string | null | undefined, username: string): string {
  const p = toPlatform(platform);
  return p === 'tiktok' ? username : `${p}:${username}`;
}

export function parseLeadKey(key: string): { platform: Platform; username: string } {
  const m = /^(instagram|x|tiktok):(.+)$/.exec(key);
  return m ? { platform: m[1] as Platform, username: m[2]! } : { platform: 'tiktok', username: key };
}

/** Platforms with sounds: TikTok sounds and Instagram reel audio. */
export type SoundPlatform = 'tiktok' | 'instagram';

/**
 * A sound's page on its platform — mirrors spade-discovery's soundPageUrl.
 * TikTok ignores the slug before the id, so any word works.
 */
export function soundUrl(platform: SoundPlatform | string | null | undefined, id: string): string {
  const s = encodeURIComponent(id);
  return platform === 'instagram' ? `https://www.instagram.com/reels/audio/${s}/` : `https://www.tiktok.com/music/x-${s}`;
}
